import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleText.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleText.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    const source = event.detail.data?.source;

    console.log('Cart icon received update:', { itemCount, source, comingFromProductForm, currentCount: this.currentCartCount });

    // Handle updating state for immediate feedback
    if (itemCount === 'updating') {
      this.refs.cartBubble.classList.remove('visually-hidden');
      this.refs.cartBubble.classList.add('cart-bubble--animating');
      this.refs.cartBubbleText.textContent = '...';
      return;
    }

    // Simple validation - let Shopify handle the complexity
    const validItemCount = typeof itemCount === 'number' && !isNaN(itemCount) && itemCount >= 0 ? itemCount : 0;
    
    // Don't let product-form-component override with lower counts unless it's a significant change
    if (source === 'product-form-component' && validItemCount < this.currentCartCount && validItemCount > 0) {
      console.log('Ignoring product-form-component update that would decrease count:', { 
        validItemCount, 
        currentCount: this.currentCartCount, 
        source 
      });
      return;
    }
    
    // Always update to the latest count - let the cart API be the source of truth
    // The protection logic was causing legitimate updates to be ignored
    
    console.log('Cart icon updating to count:', validItemCount);
    this.renderCartBubble(validItemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // Always use the exact itemCount - never add to existing count
    const finalCount = typeof itemCount === 'number' ? itemCount : 0;
    
    console.log('Rendering cart bubble:', { 
      itemCount, 
      finalCount, 
      comingFromProductForm,
      currentCount: this.currentCartCount 
    });
    
    this.refs.cartBubble.classList.toggle('visually-hidden', finalCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', finalCount > 0 && animate);
    this.currentCartCount = finalCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', finalCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleText.textContent;
    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
